import { composerRequest } from '../composerRequest';
import { mailIdentity } from '../mailDashboardApi';
import { useMailRuntime } from "../context/MailRuntime";
import { useCallback, useEffect, useRef } from "react";
import { useComposerContext } from "../context/ComposerContext";
import { makeThumbnailImageBlock } from "../blocks/factories";

export default function useImageUpload({ getRoot, appendBlockToRoot }) {
  const ctx = useComposerContext();
  const alive = useRef(true);
  useEffect(()=>{alive.current=true;return()=>{alive.current=false;};},[]);
  const runtime = useMailRuntime();
  const { fetch } = runtime;
  const { API_PRESIGN_IMAGE } = runtime.api;
  const _activeImageRef = useRef(null);
  const _activeSlotRef = useRef(null);

  const normalizeUrl = useCallback((raw = "") => {
    let url = String(raw || "").trim();
    if (!url) return "";

    const lower = url.toLowerCase();
    if (lower.startsWith("javascript:") || lower.startsWith("data:")) return "";

    if (!/^https?:\/\//i.test(url)) {
      url = "https://" + url.replace(/^\/+/, "");
    }
    return url;
  }, []);

  const wireThumbDomImg = useCallback((imgEl) => {
    if (!imgEl) return;
    imgEl.setAttribute("data-thumb", "");
    imgEl.setAttribute("contenteditable", "false");
    imgEl.style.cursor = "pointer";
    imgEl.style.outline = "none";

    imgEl.onclick = () =>
      window.__bcHandleImageClick && window.__bcHandleImageClick(imgEl);
  }, []);

  /* ---- Detect popover mode from element context ---- */
  const detectMode = useCallback((el) => {
    if (!el) return "gallery";
    if (el.closest("[data-book-cover]") || el.hasAttribute("data-cover-slot")) return "cover";
    if (el.closest("[data-bc-section='banner-image']") || el.hasAttribute("data-banner-slot")) return "banner";
    return "gallery";
  }, []);

  const ensureGlobalImageHandlers = useCallback(() => {

    window.__bcImageHandlersReady = true;

    /* ---- Click existing image ---- */
    window.__bcHandleImageClick = (imgEl) => {
      _activeImageRef.current = imgEl;
      _activeSlotRef.current = null;

      document
        .querySelectorAll("img[data-thumb]")
        .forEach((i) => (i.style.outline = "none"));

      imgEl.style.cursor = "pointer";
      imgEl.setAttribute("contenteditable", "false");

      setTimeout(() => {
        window.__bcOpenImageActions?.();
      }, 0);

      window.__bcAddImageToRow = (el) => {
        const row = el.closest("[data-thumb-row]");
        if (!row) return;

        const imgs = row.querySelectorAll("img[data-thumb]");
        if (imgs.length >= 3) {
          alert("Max 3 images per row.");
          return;
        }

        row.dataset.pendingAdd = "true";

        ctx.fileInputRef.current.dataset.mode = "thumb-add";
        ctx.fileInputRef.current.click();
      };
    };

    /* ---- Click placeholder slot ---- */
    window.__bcImageSlotClick = (slotEl) => {
      _activeSlotRef.current = slotEl;
      _activeImageRef.current = null;

      setTimeout(() => {
        window.__bcOpenSlotActions?.();
      }, 0);
    };

    window.__bcSelectImage = (imgEl) => {
      _activeImageRef.current = imgEl;

      document
        .querySelectorAll("img[data-thumb]")
        .forEach((i) => (i.style.outline = "none"));

      imgEl.style.cursor = "pointer";
      imgEl.setAttribute("contenteditable", "false");
    };

    window.__bcGetActiveImage = () => _activeImageRef.current;
    window.__bcGetActiveSlot = () => _activeSlotRef.current;

    window.__bcOpenImageActions = null;
    window.__bcOpenSlotActions = null;
    const names = ['__bcHandleImageClick','__bcImageSlotClick','__bcSelectImage','__bcGetActiveImage','__bcGetActiveSlot'];
    const owned = Object.fromEntries(names.map(name => [name, window[name]]));
    return () => { for (const name of names) if (window[name] === owned[name]) delete window[name]; window.__bcImageHandlersReady = false; };

  }, [ctx.fileInputRef]);

  /* ---- Smart popover positioning (stays in viewport, fixed-position coords) ---- */
  const calcPopoverPos = useCallback((rect, popoverHeight = 280, popoverWidth = 260) => {
    const viewH = window.innerHeight;
    const viewW = window.innerWidth;
    const pad = 12;

    // Prefer: to the right of the element
    let left = rect.right + pad;
    // If that pushes off the right edge, try to the left
    if (rect.right + pad + popoverWidth > viewW) {
      left = rect.left - popoverWidth - pad;
    }
    // Clamp to screen edges
    left = Math.max(8, Math.min(left, viewW - popoverWidth - 8));

    // Prefer: aligned with top of element
    let top = rect.top - pad;
    // If that pushes below viewport, move it up
    if (top + popoverHeight > viewH) {
      top = viewH - popoverHeight - pad;
    }
    // Never above the sticky header (approx 90px)
    top = Math.max(90, top);

    return { top, left };
  }, []);

  /* ---- Open popover for existing image ---- */
  const openImageActions = useCallback(() => {
    const active = window.__bcGetActiveImage?.();
    if (!active) return;

    const mode = detectMode(active);
    const row = active.closest("[data-thumb-row]");
    const rowSize = row?.dataset?.size || "medium";

    const rect = active.getBoundingClientRect();
    const pos = calcPopoverPos(rect);
    ctx.setImagePopover({
      open: true,
      top: pos.top,
      left: pos.left,
      link: active.dataset.link || "",
      size: rowSize,
      mode,
      isPlaceholder: false,
    });

    if (active.dataset.link) {
      active.style.boxShadow = "0 0 0 2px rgba(63,37,107,0.4)";
      active.title = `Linked: ${active.dataset.link}`;
    } else {
      active.style.boxShadow = "none";
      active.title = "";
    }
  }, [ctx, detectMode, calcPopoverPos]);

  /* ---- Open popover for placeholder slot ---- */
  const openSlotActions = useCallback(() => {
    const slot = window.__bcGetActiveSlot?.();
    if (!slot) return;

    const mode = detectMode(slot);
    const row = slot.closest("[data-thumb-row]");
    const rowSize = row?.dataset?.size || "large";

    const rect = slot.getBoundingClientRect();
    const pos = calcPopoverPos(rect);
    ctx.setImagePopover({
      open: true,
      top: pos.top,
      left: pos.left,
      link: "",
      size: rowSize,
      mode,
      isPlaceholder: true,
    });
  }, [ctx, detectMode, calcPopoverPos]);

  const applyImageLink = useCallback(() => {
    const active = window.__bcGetActiveImage?.();
    if (!active) return;

    const normalized = normalizeUrl(ctx.imagePopover.link || "");
    if (ctx.imagePopover.link && !normalized) {
      alert("Please enter a valid URL (example: https://example.com)");
      return;
    }

    if (normalized) {
      active.dataset.link = normalized;
      active.title = `Linked: ${normalized}`;
      active.style.boxShadow = "0 0 0 2px rgba(63,37,107,0.4)";
    } else {
      delete active.dataset.link;
      active.title = "";
      active.style.boxShadow = "none";
    }

    active.onclick = () => window.__bcHandleImageClick(active);
    ctx.setImagePopover((prev) => ({ ...prev, link: normalized }));
  }, [ctx, normalizeUrl]);

  const onReplaceActiveImage = useCallback(() => {
    const active = window.__bcGetActiveImage?.();
    if (!active) return;
    active.dataset.replace = "true";
    ctx.fileInputRef.current.dataset.mode = "replace-thumb";
    ctx.fileInputRef.current.click();
  }, [ctx.fileInputRef]);

  /* ---- Upload to a placeholder slot ---- */
  const onUploadToSlot = useCallback(() => {
    const slot = window.__bcGetActiveSlot?.();
    if (!slot) return;
    ctx.fileInputRef.current.dataset.mode = "slot-upload";
    ctx.fileInputRef.current.click();
  }, [ctx.fileInputRef]);

  /* ---- Remove an existing image from gallery ---- */
  const onRemoveActiveImage = useCallback(() => {
    const active = window.__bcGetActiveImage?.();
    if (!active) return;

    const row = active.closest("[data-thumb-row]");
    active.remove();
    _activeImageRef.current = null;

    // If the row is now empty, add a placeholder slot back
    if (row && row.querySelectorAll("img[data-thumb]").length === 0 && !row.querySelector("[data-image-slot]")) {
      const slot = document.createElement("div");
      slot.setAttribute("data-image-slot", "");
      slot.setAttribute("contenteditable", "false");
      slot.style.cssText = "width:160px;height:160px;border:2px dashed rgba(0,0,0,0.15);border-radius:12px;display:flex;align-items:center;justify-content:center;cursor:pointer;background:rgba(0,0,0,0.03);";
      slot.onclick = () => window.__bcImageSlotClick && window.__bcImageSlotClick(slot);
      slot.innerHTML = `<div style="text-align:center;color:rgba(0,0,0,0.35);font-size:11px;pointer-events:none;"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" style="margin:0 auto 4px;"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><circle cx="8.5" cy="8.5" r="1.5"></circle><path d="m21 15-5-5L5 21"></path></svg><div>Click to add image</div></div>`;
      row.appendChild(slot);
    }

    ctx.setImagePopover((prev) => ({ ...prev, open: false }));
  }, [ctx]);

  const setThumbnailRowSize = useCallback((size) => {
    const active = window.__bcGetActiveImage?.();
    if (!active) return;

    const row = active.closest("[data-thumb-row]");
    if (!row) return;

    const sizeMap = { small: 90, medium: 120, large: 160 };

    row.dataset.size = size;

    row.querySelectorAll("img[data-thumb]").forEach((img) => {
      img.style.width = `${sizeMap[size]}px`;
      img.style.cursor = "pointer";
      img.setAttribute("contenteditable", "false");
    });

    // Also resize placeholder slots
    row.querySelectorAll("[data-image-slot]").forEach((slot) => {
      slot.style.width = `${sizeMap[size]}px`;
      slot.style.height = `${sizeMap[size]}px`;
    });

    ctx.setImagePopover((prev) => ({ ...prev, size }));
  }, [ctx]);

  const addImageToActiveRow = useCallback(() => {
    const active = window.__bcGetActiveImage?.();
    const slot = window.__bcGetActiveSlot?.();
    const source = active || slot;
    if (!source) return;

    const row = source.closest("[data-thumb-row]");
    if (!row) return;

    const existing = row.querySelectorAll("img[data-thumb]");
    const existingSlots = row.querySelectorAll("[data-image-slot]");
    if (existing.length + existingSlots.length >= 3) {
      alert("Max 3 images per row.");
      return;
    }

    const size = row.dataset.size || "large";
    const sizeMap = { small: 90, medium: 120, large: 160 };

    const newSlot = document.createElement("div");
    newSlot.setAttribute("data-image-slot", "");
    newSlot.setAttribute("contenteditable", "false");
    newSlot.style.cssText = `width:${sizeMap[size]}px;height:${sizeMap[size]}px;border:2px dashed rgba(0,0,0,0.15);border-radius:12px;display:flex;align-items:center;justify-content:center;cursor:pointer;background:rgba(0,0,0,0.03);`;
    newSlot.onclick = () => window.__bcImageSlotClick && window.__bcImageSlotClick(newSlot);
    newSlot.innerHTML = `<div style="text-align:center;color:rgba(0,0,0,0.35);font-size:11px;pointer-events:none;"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" style="margin:0 auto 4px;"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><circle cx="8.5" cy="8.5" r="1.5"></circle><path d="m21 15-5-5L5 21"></path></svg><div>Click to add image</div></div>`;
    row.appendChild(newSlot);
  }, []);

  const uploadImage = useCallback(async file => {
    if (ctx.operationLock.current) throw new Error('Wait for the current operation to finish.');
    if (runtime.upload && (!['image/jpeg','image/png','image/webp'].includes(file.type) || file.size < 1 || file.size > 2 * 1024 * 1024)) throw new Error('Choose a JPEG, PNG, or WebP up to 2 MB for your newsletter.');
    if (!['image/jpeg','image/png','image/webp','image/gif'].includes(file.type) || file.size < 1 || file.size > 5 * 1024 * 1024 || !/\.(jpe?g|png|webp|gif)$/i.test(file.name)) throw new Error('Choose a JPEG, PNG, WebP, or GIF up to 5 MB.');
    ctx.operationLock.current = true; ctx.setUploading(true);
    try {
      if ((await mailIdentity(runtime)).attributes.sub !== ctx.owner) throw new Error('Your account changed. Reopen the composer.');
      if (runtime.upload) { const url = await runtime.upload(file); if ((await mailIdentity(runtime)).attributes.sub !== ctx.owner) throw new Error('Your account changed. Image was not inserted.'); if (!alive.current) throw new Error('Composer closed. Reopen your draft before adding images.'); return url; }
      const data = await composerRequest(runtime, ctx.owner, API_PRESIGN_IMAGE, 'POST', { fileName: file.name, contentType: file.type, fileSize: file.size, uploadType: 'newsletter-image' });
      for (const target of [data.author, data.master]) {
        if (!target?.url || !/^https:\/\//.test(target.url)) throw new Error('Invalid image upload destination.');
        const response = await fetch(target.url, { method:'PUT', headers:{'Content-Type':file.type}, body:file, signal:AbortSignal.timeout(60000) });
        if (!response.ok) throw new Error('Image upload did not finish. Retry before adding it to your newsletter.');
      }
      if ((await mailIdentity(runtime)).attributes.sub !== ctx.owner) throw new Error('Your account changed. Image was not inserted.');
      if (typeof data.publicUrl !== 'string' || !data.publicUrl.startsWith('https://')) throw new Error('Image address was not returned.');
      return data.publicUrl;
    } finally { ctx.operationLock.current = false; ctx.setUploading(false); }
  }, [ctx, runtime, fetch, API_PRESIGN_IMAGE]);

  /* ---- Replace a placeholder slot with a real image ---- */
  const replaceSlotWithImage = useCallback((slot, url, alt = "") => {
    if (!slot) return;

    const mode = detectMode(slot);

    if (mode === "cover") {
      // Book card cover: replace slot with img inside the data-book-cover container
      const cover = slot.closest("[data-book-cover]") || slot.parentElement;
      const img = document.createElement("img");
      img.src = url;
      img.alt = alt;
      img.setAttribute("data-thumb", "");
      img.setAttribute("contenteditable", "false");
      img.style.cssText = "width:140px;height:auto;border-radius:8px;box-shadow:0 4px 12px rgba(0,0,0,0.15);display:block;cursor:pointer;";
      img.onclick = () => window.__bcHandleImageClick && window.__bcHandleImageClick(img);
      slot.replaceWith(img);
      // Select the new image
      window.__bcHandleImageClick(img);
      return;
    }

    if (mode === "banner") {
      // Banner: replace slot with full-width img
      const container = slot.parentElement;
      const img = document.createElement("img");
      img.src = url;
      img.alt = alt;
      img.setAttribute("data-banner-img", "");
      img.setAttribute("contenteditable", "false");
      img.style.cssText = "width:100%;display:block;margin:0;padding:0;border:0;cursor:pointer;";
      img.onclick = () => window.__bcHandleImageClick && window.__bcHandleImageClick(img);
      slot.replaceWith(img);
      window.__bcHandleImageClick(img);
      return;
    }

    // Gallery: replace slot with thumbnail img
    const row = slot.closest("[data-thumb-row]");
    const size = row?.dataset?.size || "large";
    const sizeMap = { small: 90, medium: 120, large: 160 };

    const img = document.createElement("img");
    img.src = url;
    img.alt = alt;
    img.style.width = `${sizeMap[size]}px`;
    img.style.display = "block";

    wireThumbDomImg(img);

    slot.replaceWith(img);

    // Auto-add next placeholder if room for more images (max 3)
    if (row) {
      const totalImages = row.querySelectorAll("img[data-thumb]").length;
      const totalSlots = row.querySelectorAll("[data-image-slot]").length;
      if (totalImages + totalSlots < 3) {
        const nextSlot = document.createElement("div");
        nextSlot.setAttribute("data-image-slot", "");
        nextSlot.setAttribute("contenteditable", "false");
        nextSlot.style.cssText = `width:${sizeMap[size]}px;height:${sizeMap[size]}px;border:2px dashed rgba(0,0,0,0.15);border-radius:12px;display:flex;align-items:center;justify-content:center;cursor:pointer;background:rgba(0,0,0,0.03);`;
        nextSlot.innerHTML = `<div style="text-align:center;color:rgba(0,0,0,0.35);font-size:11px;pointer-events:none;"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" style="margin:0 auto 4px;"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><circle cx="8.5" cy="8.5" r="1.5"></circle><path d="m21 15-5-5L5 21"></path></svg><div>Click to add</div></div>`;
        row.appendChild(nextSlot);
      }
    }

    // Don't auto-open popover for the new image — let user see the result
    _activeImageRef.current = img;
    _activeSlotRef.current = null;
    ctx.setImagePopover((prev) => ({ ...prev, open: false }));
  }, [detectMode, wireThumbDomImg, ctx]);

  const onFileChange = useCallback(async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;

    const mode = ctx.fileInputRef.current.dataset.mode || "thumb-new";

    try {
      const url = await uploadImage(f);

      if (mode === "slot-upload") {
        const slot = window.__bcGetActiveSlot?.();
        if (slot) {
          replaceSlotWithImage(slot, url, f.name);
          _activeSlotRef.current = null;
        }
        return;
      }

      if (mode === "replace-thumb") {
        const active = window.__bcGetActiveImage?.();
        if (active) {
          active.src = url;
          delete active.dataset.replace;

          active.onclick = () =>
            window.__bcHandleImageClick &&
            window.__bcHandleImageClick(active);

          active.style.cursor = "pointer";
          active.setAttribute("contenteditable", "false");
        }
        return;
      }

      if (mode === "thumb-add") {
        const root = getRoot();
        if (!root) return;

        const pendingRow = root.querySelector(
          `[data-thumb-row][data-pending-add="true"]`
        );

        if (pendingRow) {
          delete pendingRow.dataset.pendingAdd;

          const imgs = pendingRow.querySelectorAll("img[data-thumb]");
          if (imgs.length >= 3) {
            alert("You can add up to 3 images per row.");
            return;
          }

          const size = pendingRow.dataset.size || "medium";
          const sizeMap = { small: 90, medium: 120, large: 160 };

          const img = document.createElement("img");
          img.src = url;
          img.alt = f.name;
          img.style.width = `${sizeMap[size]}px`;
          img.style.display = "block";

          wireThumbDomImg(img);

          pendingRow.appendChild(img);

          window.__bcHandleImageClick && window.__bcHandleImageClick(img);

          return;
        }
      }

      appendBlockToRoot(makeThumbnailImageBlock([{ src: url, alt: f.name }]));
    } catch (error) {
      alert(error.message || "Upload failed.");
    } finally {
      ctx.fileInputRef.current.dataset.mode = "";
      e.target.value = "";
    }
  }, [ctx.fileInputRef, uploadImage, getRoot, wireThumbDomImg, appendBlockToRoot, replaceSlotWithImage]);

  const onInsertImageClick = useCallback(() => {
    ctx.fileInputRef.current.dataset.mode = "thumb-new";
    ctx.fileInputRef.current.click();
  }, [ctx.fileInputRef]);

  const onInsertBannerImageClick = useCallback(() => {
    ctx.fileInputRef.current.dataset.mode = "banner";
    ctx.fileInputRef.current.click();
  }, [ctx.fileInputRef]);

  return {
    normalizeUrl,
    wireThumbDomImg,
    ensureGlobalImageHandlers,
    openImageActions,
    openSlotActions,
    applyImageLink,
    onReplaceActiveImage,
    onUploadToSlot,
    onRemoveActiveImage,
    setThumbnailRowSize,
    addImageToActiveRow,
    uploadImage,
    replaceSlotWithImage,
    onFileChange,
    onInsertImageClick,
    onInsertBannerImageClick,
  };
}
